import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge, Box, Button, Chip, CircularProgress, FormControl,
  InputAdornment, InputLabel, LinearProgress, MenuItem, Pagination,
  Paper, Select, Snackbar, Stack, Tab, Tabs, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { intelligenceApi } from '../api/intelligenceApi';
import ContactProfileDrawer from '../components/intelligence/ContactProfileDrawer';
import InsightsDashboard from '../components/intelligence/InsightsDashboard';
import SalesIntelligenceTab from '../components/intelligence/SalesIntelligenceTab';
import WeeklyPlanView from '../components/intelligence/WeeklyPlanView';
import CampaignPreviewModal from '../components/intelligence/CampaignPreviewModal';
import { CampaignDetailDialog } from './EmailBroadcastsDashboard';
import SalesFunnelInsights from '../components/intelligence/SalesFunnelInsights';
import WeeklyContentPanel from '../components/intelligence/WeeklyContentPanel';

// ── Constants ──────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  cold:      { bg: '#e3f2fd', text: '#1565c0' },
  warm:      { bg: '#fff3e0', text: '#e65100' },
  hot:       { bg: '#fce4ec', text: '#b71c1c' },
  urgent:    { bg: '#b71c1c', text: '#ffffff' },
  inactive:  { bg: '#f5f5f5', text: '#757575' },
  converted: { bg: '#e8f5e9', text: '#2e7d32' },
};

const INTEREST_LABELS = {
  flights:        'Flights',
  visa:           'Visa',
  hotel:          'Hotel',
  holiday_package:'Holiday Package',
  corporate:      'Corporate',
  group_travel:   'Group Travel',
  mixed:          'Mixed',
  unknown:        'Unknown',
};

const ORG_TYPE_CONFIG = {
  individual:   { label: 'Individual',    color: '#1565c0', bg: '#e3f2fd' },
  corporate:    { label: 'Corporate',     color: '#4a148c', bg: '#f3e5f5' },
  school:       { label: 'School',        color: '#1b5e20', bg: '#e8f5e9' },
  church:       { label: 'Church',        color: '#bf360c', bg: '#fbe9e7' },
  government:   { label: 'Government',   color: '#0d47a1', bg: '#e8eaf6' },
  ngo:          { label: 'NGO / Charity', color: '#e65100', bg: '#fff3e0' },
  travel_agent: { label: 'Travel Agent',  color: '#006064', bg: '#e0f7fa' },
  supplier:     { label: 'Supplier',      color: '#37474f', bg: '#eceff1' },
  unknown:      { label: 'Unknown',       color: '#757575', bg: '#f5f5f5' },
};

const DEFAULT_NEXT_ACTION = {
  hot:       'Send closing offer',
  warm:      'Follow up with deal',
  cold:      'Intro campaign',
  inactive:  'Re-engagement email',
  converted: 'Upsell / referral',
};

// ── Helpers ────────────────────────────────────────────────────────────────
function timeAgo(isoString) {
  if (!isoString) return '—';
  const secs = Math.floor((Date.now() - new Date(isoString)) / 1000);
  if (secs < 60)   return 'Just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60)   return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)    return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30)   return `${days}d ago`;
  const mos = Math.floor(days / 30);
  if (mos < 12)    return `${mos}mo ago`;
  return `${Math.floor(mos / 12)}y ago`;
}

function nextAction(contact) {
  if (contact.recommended_action) return contact.recommended_action;
  return DEFAULT_NEXT_ACTION[contact.operational_status] ?? '—';
}

// ── Sub-components ─────────────────────────────────────────────────────────
function StatusChip({ status }) {
  const colors = STATUS_COLORS[status] || { bg: '#eeeeee', text: '#333' };
  return (
    <Chip
      label={status?.charAt(0).toUpperCase() + status?.slice(1)}
      size="small"
      sx={{ bgcolor: colors.bg, color: colors.text, fontWeight: 600, fontSize: 11 }}
    />
  );
}

function ReadinessBar({ value }) {
  const color = value >= 75 ? 'error' : value >= 45 ? 'warning' : 'primary';
  return (
    <Box sx={{ width: 80 }}>
      <Typography variant="caption">{value}/100</Typography>
      <LinearProgress variant="determinate" value={value} color={color} sx={{ height: 5, borderRadius: 3 }} />
    </Box>
  );
}


// ── Main page ──────────────────────────────────────────────────────────────
export default function IntelligenceHubPage() {
  const navigate = useNavigate();
  const [reportCampaignId, setReportCampaignId] = useState(null);
  const [reportOpen, setReportOpen]             = useState(false);
  const [tab, setTab]                   = useState(0);
  const [contacts, setContacts]         = useState(null);
  const [loading, setLoading]           = useState(false);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [interestFilter, setInterestFilter] = useState('');
  const [orgTypeFilter, setOrgTypeFilter] = useState('');
  const [page, setPage]                 = useState(1);
  const [selectedContactId, setSelectedContactId] = useState(null);

  const [weeklyPlan, setWeeklyPlan]     = useState(null);
  const [planLoading, setPlanLoading]   = useState(false);
  const [discovering, setDiscovering]   = useState(false);
  const [clearing, setClearing]         = useState(false);
  const [snackbar, setSnackbar]         = useState(null);

  const [previewOpen, setPreviewOpen]   = useState(false);
  const [previewData, setPreviewData]   = useState(null); // full suggestion w/ recommendation

  const [insights, setInsights]         = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsSummary, setInsightsSummary] = useState(null);
  const [weeklyContentBadge, setWeeklyContentBadge] = useState(0);

  // ── Data fetchers ──────────────────────────────────────────────────────
  const fetchContacts = useCallback(() => {
    setLoading(true);
    intelligenceApi.getContacts({ search, status: statusFilter, interest: interestFilter, org_type: orgTypeFilter, page })
      .then(setContacts)
      .finally(() => setLoading(false));
  }, [search, statusFilter, interestFilter, orgTypeFilter, page]);

  const fetchWeeklyPlan = () => {
    setPlanLoading(true);
    intelligenceApi.getWeeklyPlan()
      .then(setWeeklyPlan)
      .finally(() => setPlanLoading(false));
  };

  const fetchInsights = (silent = false) => {
    if (!silent) setInsightsLoading(true);
    intelligenceApi.getInsights()
      .then((data) => {
        setInsights(data);
        setInsightsSummary(data?.summary ?? null);
      })
      .catch(() => {
        // leave insights as-is; InsightsDashboard shows null state gracefully
      })
      .finally(() => { if (!silent) setInsightsLoading(false); });
  };

  // Load insights silently on mount so data is ready before the user clicks the tab.
  // Also prefetch weekly content badge count (travel tips + promotions pending approval).
  useEffect(() => {
    fetchInsights(true);
    fetchWeeklyPlan();
    intelligenceApi.getWeeklyContent()
      .then((d) => {
        const pending = (d?.recommendations ?? []).filter((r) => r.status === 'pending_approval').length;
        setWeeklyContentBadge(pending);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab === 0) fetchContacts();
    if (tab === 1) fetchWeeklyPlan();
    if (tab === 2 && !insights) fetchInsights(); // skip if already loaded on mount
  }, [tab, fetchContacts, insights]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce search changes only
  useEffect(() => {
    const t = setTimeout(() => { if (tab === 0) fetchContacts(); }, 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Weekly plan helpers ────────────────────────────────────────────────
  const allPlanSuggestions = weeklyPlan?.weeks?.flatMap((w) => w.suggestions ?? []) ?? [];
  const pendingSuggestions = allPlanSuggestions.filter((s) => s.status === 'pending_review').length;

  const updateSuggestionInPlan = (updated) => {
    setWeeklyPlan((prev) => ({
      ...prev,
      weeks: (prev.weeks ?? []).map((w) => ({
        ...w,
        suggestions: w.suggestions.map((s) => s.id === updated.id ? updated : s),
      })),
    }));
  };

  const handleAudienceApprove = (id) =>
    intelligenceApi.approveAudience(id).then(updateSuggestionInPlan);

  const handleApproveAndPreview = async (id) => {
    const suggestion = await intelligenceApi.generatePreview(id);
    updateSuggestionInPlan(suggestion);
    setPreviewData(suggestion);
    setPreviewOpen(true);
  };

  const handleScheduled = async (recId, scheduleAt, subject, bodyHtml, personalisationMode = 'none', personalisedOverrides = null) => {
    const result = await intelligenceApi.convertRecommendation(recId, scheduleAt, subject, bodyHtml, personalisationMode, personalisedOverrides);
    fetchWeeklyPlan();
    const d = new Date(result.schedule_at);
    setSnackbar(`Campaign scheduled for ${d.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`);
  };

  const handleAudienceReject = (id) =>
    intelligenceApi.rejectAudience(id).then(updateSuggestionInPlan);

  const handleRecommendationApprove = (suggestionId, recId) =>
    intelligenceApi.approveRecommendation(recId).then((rec) => {
      setWeeklyPlan((prev) => ({
        ...prev,
        weeks: (prev.weeks ?? []).map((w) => ({
          ...w,
          suggestions: w.suggestions.map((s) =>
            s.id === suggestionId ? { ...s, recommendation: rec } : s
          ),
        })),
      }));
    });

  const handleRecommendationReject = (suggestionId, recId) =>
    intelligenceApi.rejectRecommendation(recId).then((rec) => {
      setWeeklyPlan((prev) => ({
        ...prev,
        weeks: (prev.weeks ?? []).map((w) => ({
          ...w,
          suggestions: w.suggestions.map((s) =>
            s.id === suggestionId ? { ...s, recommendation: rec } : s
          ),
        })),
      }));
    });

  const handleConvert = (suggestionId, recId, personalisationMode = 'none') =>
    intelligenceApi.convertRecommendation(recId, null, null, null, personalisationMode).then(() => fetchWeeklyPlan());

  const handleRunDiscovery = () => {
    setDiscovering(true);
    intelligenceApi.runDiscovery()
      .then((res) => {
        setSnackbar(res.message);
        setTimeout(() => fetchWeeklyPlan(), 3000);
      })
      .catch(() => setSnackbar('Failed to dispatch discovery job.'))
      .finally(() => setDiscovering(false));
  };

  const handleClearPlan = () => {
    if (!window.confirm('Clear the entire monthly plan? Converted campaigns are kept — only pending and approved suggestions will be removed.')) return;
    setClearing(true);
    intelligenceApi.clearPlan()
      .then((res) => {
        setSnackbar(res.message);
        setWeeklyPlan(null);
      })
      .catch(() => setSnackbar('Failed to clear plan.'))
      .finally(() => setClearing(false));
  };

  const handleViewHot = () => {
    setStatusFilter('hot');
    setInterestFilter('');
    setPage(1);
    setTab(0);
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>
        Intelligence Hub
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        AI-powered customer profiles built from inquiry and inbox behaviour
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: '1px solid #e0e0e0' }}>
        <Tab label="Contact Profiles" />
        <Tab
          label={
            pendingSuggestions > 0
              ? <Badge badgeContent={pendingSuggestions} color="error" sx={{ pr: 1.5 }}>Weekly Plan</Badge>
              : 'Weekly Plan'
          }
        />
        <Tab label="Insights" />
        <Tab
          label={
            weeklyContentBadge > 0
              ? <Badge badgeContent={weeklyContentBadge} color="error" sx={{ pr: 1.5 }}>Weekly Content</Badge>
              : 'Weekly Content'
          }
        />
        {/* Sales Intelligence tab hidden until wired — re-add Tab here to restore */}
      </Tabs>

      {/* ── Contact Profiles tab ── */}
      {tab === 0 && (
        <Box>
          <Stack direction="row" spacing={2} mb={2} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 260 }}
            />

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <MenuItem value="">All statuses</MenuItem>
                {['hot', 'warm', 'cold', 'inactive', 'converted'].map((s) => (
                  <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Interest</InputLabel>
              <Select
                value={interestFilter}
                label="Interest"
                onChange={(e) => { setInterestFilter(e.target.value); setPage(1); }}
              >
                <MenuItem value="">All interests</MenuItem>
                {Object.entries(INTEREST_LABELS).map(([v, l]) => (
                  <MenuItem key={v} value={v}>{l}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Org Type</InputLabel>
              <Select
                value={orgTypeFilter}
                label="Org Type"
                onChange={(e) => { setOrgTypeFilter(e.target.value); setPage(1); }}
              >
                <MenuItem value="">All org types</MenuItem>
                {Object.entries(ORG_TYPE_CONFIG)
                  .filter(([v]) => v !== 'unknown')
                  .map(([v, c]) => (
                    <MenuItem key={v} value={v}>{c.label}</MenuItem>
                  ))}
              </Select>
            </FormControl>

            {(statusFilter || interestFilter || orgTypeFilter || search) && (
              <Button
                size="small"
                variant="text"
                onClick={() => { setStatusFilter(''); setInterestFilter(''); setOrgTypeFilter(''); setSearch(''); setPage(1); }}
              >
                Clear filters
              </Button>
            )}

            {loading && <CircularProgress size={20} />}

            {contacts?.total > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                {contacts.total.toLocaleString()} contact{contacts.total !== 1 ? 's' : ''}
              </Typography>
            )}
          </Stack>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Org Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Interest</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Readiness</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Emails</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tags</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Recommended Action</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last Active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts?.data?.map((contact) => (
                  <TableRow
                    key={contact.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedContactId(contact.id)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{contact.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {contact.email}
                        {contact.company ? ` · ${contact.company}` : ''}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <StatusChip status={contact.operational_status} />
                    </TableCell>

                    <TableCell>
                      {contact.org_type && contact.org_type !== 'unknown' ? (() => {
                        const cfg = ORG_TYPE_CONFIG[contact.org_type] || ORG_TYPE_CONFIG.unknown;
                        return (
                          <Chip
                            label={cfg.label}
                            size="small"
                            sx={{ fontSize: 10, fontWeight: 600, bgcolor: cfg.bg, color: cfg.color }}
                          />
                        );
                      })() : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {INTEREST_LABELS[contact.primary_interest] ?? contact.primary_interest ?? '—'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <ReadinessBar value={contact.buying_readiness ?? 0} />
                    </TableCell>

                    <TableCell sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight={contact.total_inbound_emails > 2 ? 700 : 400}>
                        {contact.total_inbound_emails ?? 0}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {contact.tags?.slice(0, 2).map((t) => (
                          <Chip key={t.tag} label={t.tag} size="small" sx={{ fontSize: 10 }} />
                        ))}
                        {contact.tags?.length > 2 && (
                          <Chip
                            label={`+${contact.tags.length - 2}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 10 }}
                          />
                        )}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" color="text.secondary" noWrap title={nextAction(contact)}>
                        {nextAction(contact)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {timeAgo(contact.last_active_at || contact.last_classified_at)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

                {!loading && contacts?.data?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary" variant="body2">
                        {statusFilter || interestFilter || search
                          ? 'No contacts match your current filters.'
                          : 'No profiles yet. Run: php artisan intelligence:backfill-from-contacts'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {contacts?.last_page > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={contacts.last_page}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
              />
            </Box>
          )}
        </Box>
      )}

      {/* ── Weekly Plan tab ── */}
      {tab === 1 && (
        <WeeklyPlanView
          weeklyPlan={weeklyPlan}
          planLoading={planLoading}
          discovering={discovering}
          clearing={clearing}
          onRunDiscovery={handleRunDiscovery}
          onClearPlan={handleClearPlan}
          onApproveAndPreview={handleApproveAndPreview}
          onReject={handleAudienceReject}
          onRecommendationApprove={handleRecommendationApprove}
          onRecommendationReject={handleRecommendationReject}
          onConvert={handleConvert}
          onWhatsAppBroadcast={(s) =>
            navigate(`/marketing?tab=whatsapp_broadcast&intelligence_audience=${s.id}`)
          }
          onEmailBroadcast={(s) =>
            navigate(`/marketing?tab=email_broadcast&intelligence_audience=${s.id}`)
          }
          onViewReport={(campaignId) => { setReportCampaignId(campaignId); setReportOpen(true); }}
        />
      )}

      {/* ── Insights tab ── */}
      {tab === 2 && (
        <>
          <InsightsDashboard data={insights} loading={insightsLoading} />
          <SalesFunnelInsights onContactClick={(id) => setSelectedContactId(id)} />
        </>
      )}

      {/* ── Weekly Content tab (Travel Tips + Promotions) ── */}
      {tab === 3 && (
        <WeeklyContentPanel />
      )}

      {/* Sales Intelligence tab hidden until wired */}

      <CampaignPreviewModal
        open={previewOpen}
        suggestion={previewData}
        recommendation={previewData?.recommendation}
        onClose={() => setPreviewOpen(false)}
        onScheduled={handleScheduled}
        onEditInMassEmail={previewData ? () => {
          setPreviewOpen(false);
          navigate(`/marketing?tab=email_broadcast&intelligence_audience=${previewData.id}`);
        } : undefined}
      />

      <CampaignDetailDialog
        campaign={reportCampaignId ? { id: reportCampaignId } : null}
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        onChanged={() => setReportOpen(false)}
      />

      <ContactProfileDrawer
        contactId={selectedContactId}
        onClose={() => setSelectedContactId(null)}
      />

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
